import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';
import { useSnackbar } from 'notistack';
import type { Location } from 'react-router-dom';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  DataGrid,
  GridCellEditStopReasons,
  GridCellModes,
  GridCellModesModel,
  GridCellParams,
  GridColDef,
  GridEditInputCell,
  GridPaginationModel,
  GridRowModel,
  useGridApiRef,
} from '@mui/x-data-grid';

import { useUnsavedChangesGuard } from '~/components/Flights/Charter/hooks/useUnsavedChangesGuard';

import { useGetCharterFareFamilyPricing } from '~/hooks/apis/flights-charter/useCharterFareFamilyPricing';
import { rewardsQueryKeys } from '~/hooks/apis/rewards/rewardsQueryKeys';
import { useGetRewardsCatalogs } from '~/hooks/apis/rewards/useRewardsCatalogs';
import { useGetRewardsEntries, useUpsertRewardsEntries } from '~/hooks/apis/rewards/useRewardsEntries';
import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import type { FareFamilyPricingGetRes, FareFamilyPricingParams } from '~/services/CharterService';
import type { RewardsCatalogEntry } from '~/services/RewardsService';
import { formatDateSlashes } from '~/services/TimeService';

import ClassicRewardsInventoryDialog from './ClassicRewardsInventoryDialog';
import {
  CABIN_CLASSES,
  CABIN_CLASS_LABELS,
  type ClassicRewardsPricingRow,
  type ClassicRewardsSearchFilters,
  type FareFamilyCabinClass,
  buildPricingRows,
} from './classicRewards.types';

/**
 * Charter flight route pair options — currently hard-coded to MEL↔MLE for v1.
 *
 * TODO: Replace with a fetched list from svc-flights or a shared constant once
 * we support more than one charter route. Until then, edits here need to land
 * in lockstep with charter availability.
 */
const ROUTE_OPTIONS = [
  {
    label: 'MEL → MLE',
    departingDepAirport: 'MEL',
    departingArrAirport: 'MLE',
    returningDepAirport: 'MLE',
    returningArrAirport: 'MEL',
  },
] as const;

/** Page size for charter fare-family pricing fetch — matches API default. */
const CHARTER_FETCH_LIMIT = 200;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const MAX_AUTO_HEIGHT_ROWS = 5;

type PendingAction =
  | { type: 'search'; filters: ClassicRewardsSearchFilters }
  | { type: 'discard' }
  | { type: 'navigate'; location: Location };

/** Available currencies for reward entry pricing. */
const CURRENCY_OPTIONS = ['AUD', 'USD', 'NZD'] as const;

/** Default currency for v1 (Australia-based charter flights). */
const DEFAULT_CURRENCY = 'AUD';
const DEFAULT_SEARCH_FILTERS: ClassicRewardsSearchFilters = {
  departingDepAirport: ROUTE_OPTIONS[0].departingDepAirport,
  departingArrAirport: ROUTE_OPTIONS[0].departingArrAirport,
  returningDepAirport: ROUTE_OPTIONS[0].returningDepAirport,
  returningArrAirport: ROUTE_OPTIONS[0].returningArrAirport,
  cashCurrency: DEFAULT_CURRENCY,
  cabinClasses: [...CABIN_CLASSES],
};

/** Status options for entries. */
const STATUS_OPTIONS = [
  { label: 'Active', value: 'ACTIVE' as const },
  { label: 'Inactive', value: 'INACTIVE' as const },
];

/** Type for fare-family pricing API result item. */
type FareFamilyResult = FareFamilyPricingGetRes['result'][number];
const EMPTY_FARE_FAMILY_RESULTS: Array<FareFamilyResult> = [];

/** Formats a date string for display. */
function formatDisplayDate(value: string): string {
  return formatDateSlashes(value) || value;
}

/**
 * Admin page for configuring classic reward pricing per flight leg + cabin class.
 *
 * Lists flight instances from the charter fare-family pricing API, then overlays
 * reward entries from svc-lux-loyalty. Supports inline editing and bulk save.
 */
export default function ClassicRewardsPage(): React.JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const gridApiRef = useGridApiRef();
  const queryClient = useQueryClient();
  const { paginationModel, setPaginationModel, setPage } = useDataGridPagination({ page: 0, pageSize: 50 });

  // --- Form state ---
  const [selectedRoute, setSelectedRoute] = useState(0);
  const [selectedCurrency, setSelectedCurrency] = useState<string>(DEFAULT_CURRENCY);
  const [selectedCabinClasses, setSelectedCabinClasses] = useState<Array<FareFamilyCabinClass>>([...CABIN_CLASSES]);

  // Load the default search immediately. Later form changes only apply on Search.
  const [searchFilters, setSearchFilters] = useState<ClassicRewardsSearchFilters>(DEFAULT_SEARCH_FILTERS);

  const charterParams: FareFamilyPricingParams = {
    departingDepAirport: searchFilters.departingDepAirport,
    departingArrAirport: searchFilters.departingArrAirport,
    returningDepAirport: searchFilters.returningDepAirport,
    returningArrAirport: searchFilters.returningArrAirport,
    offset: 0,
    limit: CHARTER_FETCH_LIMIT,
  };
  const charterQuery = useGetCharterFareFamilyPricing(charterParams);
  const fareFamilyData: Array<FareFamilyResult> = charterQuery.data ?? EMPTY_FARE_FAMILY_RESULTS;

  // --- Edit state ---
  const [editedRows, setEditedRows] = useState<Map<string, ClassicRewardsPricingRow>>(new Map());
  const editedRowsRef = useRef(editedRows);
  const savingRef = useRef(false);
  const savedReadRef = useRef<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [baseRows, setBaseRows] = useState<Array<ClassicRewardsPricingRow>>([]);
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});
  const [isCommittingCell, setIsCommittingCell] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const hasActiveCell = Object.values(cellModesModel).some((fields) =>
    Object.values(fields).some((cell) => cell.mode === GridCellModes.Edit),
  );
  const hasPendingEdits = editedRows.size > 0 || hasActiveCell || isCommittingCell;
  const replaceDraft = useCallback((draft: Map<string, ClassicRewardsPricingRow>) => {
    // Save and page changes must see a cell committed before React's next render.
    editedRowsRef.current = draft;
    setEditedRows(draft);
  }, []);
  // --- Inventory dialog state ---
  const [inventoryEntryId, setInventoryEntryId] = useState<string | null>(null);
  const [inventoryEntryLabel, setInventoryEntryLabel] = useState('');

  // --- Step 1: Fetch the flights catalog by itemType ---
  const catalogsQuery = useGetRewardsCatalogs({ itemType: 'flight', status: 'ACTIVE' });
  const catalogs = catalogsQuery.data;
  const flightsCatalogId = catalogs?.[0]?.id ?? null;

  // --- Step 2: Collect journeyKeys from flight data for querying entries ---
  const journeyKeys = useMemo(() => {
    if (!fareFamilyData.length) {
      return [];
    }
    const keys: Array<string> = [];
    for (const flight of fareFamilyData) {
      for (const cabinClassKey of searchFilters.cabinClasses) {
        const tier = flight.tiers[cabinClassKey];
        if (tier?.journeyKey) {
          keys.push(tier.journeyKey);
        }
      }
    }
    return keys;
  }, [fareFamilyData, searchFilters]);

  // --- Step 3: Fetch reward entries matching the journeyKeys ---
  const entriesFilters = useMemo(
    () =>
      flightsCatalogId && journeyKeys.length > 0
        ? {
            catalogId: flightsCatalogId,
            itemType: 'flight',
            itemSubType: 'charter',
            itemReferences: journeyKeys,
          }
        : null,
    [flightsCatalogId, journeyKeys],
  );
  const entriesQuery = useGetRewardsEntries(entriesFilters);

  const upsertEntriesMutation = useUpsertRewardsEntries();

  // --- Merge flight data + entries into grid rows ---
  const filteredFlights = useMemo(() => {
    if (!fareFamilyData.length) {
      return [];
    }

    return fareFamilyData.map((flight) => ({
      ...flight,
      tiers: Object.fromEntries(
        searchFilters.cabinClasses.map((key) => [key, flight.tiers[key]]),
      ) as FareFamilyResult['tiers'],
    }));
  }, [fareFamilyData, searchFilters]);
  const queryRows = useMemo(
    // Only a completed pricing read can tell us which entries are unconfigured.
    () => (entriesQuery.data ? buildPricingRows(filteredFlights, entriesQuery.data, searchFilters.cashCurrency) : []),
    [filteredFlights, entriesQuery.data, searchFilters],
  );
  useEffect(() => {
    const readWasMerged =
      entriesFilters &&
      queryClient.getQueryState(rewardsQueryKeys.entriesList(entriesFilters))?.dataUpdatedAt === savedReadRef.current;
    // Keep the comparison baseline stable while the user edits or a save refreshes.
    if (
      !editedRowsRef.current.size &&
      !savingRef.current &&
      !readWasMerged &&
      !Object.keys(gridApiRef.current.state?.editRows ?? {}).length
    ) {
      setBaseRows(queryRows);
    }
  }, [queryRows, entriesQuery.dataUpdatedAt, gridApiRef, entriesFilters, queryClient]);

  /** Map of base rows by journeyKey for diff tracking. */
  const baseRowMap = useMemo(() => {
    const map = new Map<string, ClassicRewardsPricingRow>();
    for (const row of baseRows) {
      map.set(row.journeyKey, row);
    }
    return map;
  }, [baseRows]);

  /** Grid rows = base rows with edited rows overlaid. */
  const rows = useMemo(() => {
    return baseRows.map((baseRow) => editedRows.get(baseRow.journeyKey) ?? baseRow);
  }, [baseRows, editedRows]);

  // --- Handlers ---

  const discardDraft = useCallback(() => {
    for (const [id, fields] of Object.entries(gridApiRef.current.state?.editRows ?? {})) {
      for (const field of Object.keys(fields)) {
        if (gridApiRef.current.getCellMode(id, field) === GridCellModes.Edit) {
          gridApiRef.current.stopCellEditMode({ id, field, ignoreModifications: true });
        }
      }
    }
    setIsCommittingCell(false);
    replaceDraft(new Map());
  }, [gridApiRef, replaceDraft]);

  const applySearch = useCallback(
    async (filters: ClassicRewardsSearchFilters) => {
      discardDraft();
      setPage(0);
      savedReadRef.current = null;
      if (catalogsQuery.isError) await catalogsQuery.refetch();
      if (isEqual(filters, searchFilters)) {
        const [flights, entries] = await Promise.all([charterQuery.refetch(), entriesQuery.refetch()]);
        setRefreshFailed(refreshFailed && (flights.isError || entries.isError));
      } else {
        setBaseRows([]);
        setRefreshFailed(false);
        setSearchFilters(filters);
        // Route changes fetch through the query key; retry only a failed departure read.
        if (charterQuery.isError) await charterQuery.refetch();
      }
    },
    [discardDraft, setPage, searchFilters, charterQuery, catalogsQuery, entriesQuery, refreshFailed],
  );

  const handleSearch = useCallback(() => {
    const route = ROUTE_OPTIONS[selectedRoute];
    if (!route || !selectedCabinClasses.length || savingRef.current) return;
    const filters: ClassicRewardsSearchFilters = {
      departingDepAirport: route.departingDepAirport,
      departingArrAirport: route.departingArrAirport,
      returningDepAirport: route.returningDepAirport,
      returningArrAirport: route.returningArrAirport,
      cashCurrency: selectedCurrency,
      cabinClasses: CABIN_CLASSES.filter((cabin) => selectedCabinClasses.includes(cabin)),
    };
    if (hasPendingEdits) setPendingAction({ type: 'search', filters });
    else applySearch(filters);
  }, [selectedRoute, selectedCurrency, selectedCabinClasses, hasPendingEdits, applySearch]);

  const confirmDiscard = () => {
    if (!pendingAction || savingRef.current) return;
    discardDraft();
    setPendingAction(null);
    if (pendingAction.type === 'search') applySearch(pendingAction.filters);
    if (pendingAction.type === 'navigate') navigationGuard.releaseAndNavigate(pendingAction.location);
  };

  /** Tracks cell edits, clamps numeric values to >= 0, and compares against the base row. */
  const processRowUpdate = useCallback(
    (newRow: GridRowModel<ClassicRewardsPricingRow>) => {
      setIsCommittingCell(false);
      // Clamp numeric fields to prevent negative values
      const clamped = {
        ...newRow,
        pointsPrice: Math.max(0, newRow.pointsPrice ?? 0),
        cashPrice: Math.max(0, newRow.cashPrice ?? 0),
      };

      if (savingRef.current) return editedRowsRef.current.get(clamped.journeyKey) ?? clamped;
      const current = editedRowsRef.current;
      const original = baseRowMap.get(clamped.journeyKey);
      const next = new Map(current);

      const hasChanges =
        !original ||
        clamped.pointsPrice !== original.pointsPrice ||
        clamped.cashPrice !== original.cashPrice ||
        clamped.status !== original.status;

      if (hasChanges) {
        next.set(clamped.journeyKey, clamped);
      } else {
        next.delete(clamped.journeyKey);
      }

      replaceDraft(next);

      return clamped;
    },
    [baseRowMap, replaceDraft],
  );

  const commitActiveCell = useCallback(
    (showValidationWarning = true) => {
      for (const [id, fields] of Object.entries(gridApiRef.current.state?.editRows ?? {})) {
        if (Object.values(fields).some((cell) => cell.error || cell.isProcessingProps)) {
          if (showValidationWarning) {
            enqueueSnackbar('Correct the highlighted cell before saving or changing pages.', { variant: 'warning' });
          }
          return false;
        }
        for (const field of Object.keys(fields)) {
          processRowUpdate(gridApiRef.current.getRowWithUpdatedValues(id, field) as ClassicRewardsPricingRow);
          if (gridApiRef.current.getCellMode(id, field) === GridCellModes.Edit) {
            gridApiRef.current.stopCellEditMode({ id, field });
          }
        }
      }
      return true;
    },
    [gridApiRef, processRowUpdate, enqueueSnackbar],
  );

  const handleActionPointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      // Finish the local cell edit before focus changes or a confirmation dialog opens.
      event.preventDefault();
      commitActiveCell(false);
    },
    [commitActiveCell],
  );

  const onNavigationAttempt = useCallback(
    (location: Location) => {
      if (savingRef.current) {
        enqueueSnackbar('Please wait for the save to finish before leaving.', { variant: 'info' });
      } else {
        commitActiveCell(false);
        setPendingAction({ type: 'navigate', location });
      }
    },
    [enqueueSnackbar, commitActiveCell],
  );
  const navigationGuard = useUnsavedChangesGuard({
    hasUnsavedChanges: hasPendingEdits || isSaving,
    onNavigationAttempt,
  });

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      if (!savingRef.current && commitActiveCell()) setPaginationModel(model);
    },
    [commitActiveCell, setPaginationModel],
  );

  /** Highlights cells that differ from the base row. */
  const getCellClassName = useCallback(
    (params: GridCellParams<ClassicRewardsPricingRow>) => {
      const editableFields = ['pointsPrice', 'cashPrice', 'status'];
      if (!editableFields.includes(params.field)) {
        return '';
      }

      const original = baseRowMap.get(params.row.journeyKey);
      if (!original) {
        return '';
      }

      const originalValue = original[params.field as keyof ClassicRewardsPricingRow];
      return params.value !== originalValue ? 'cell-modified' : '';
    },
    [baseRowMap],
  );

  /** Enters cell edit mode on click (single-click editing). */
  const handleCellClick = useCallback(
    (params: GridCellParams) => {
      if (!params.isEditable) {
        return;
      }
      const cellMode = gridApiRef.current.getCellMode(params.id, params.field);
      if (cellMode === 'view') {
        gridApiRef.current.startCellEditMode({ id: params.id, field: params.field });
      }
    },
    [gridApiRef],
  );

  /** Saves all edited rows by bulk upserting entries in svc-lux-loyalty. */
  const handleSave = useCallback(async () => {
    if (!flightsCatalogId || savingRef.current || !commitActiveCell() || !editedRowsRef.current.size) {
      return;
    }

    // Split edited rows into "to save" and "skipped because both prices are 0".
    // 0/0 is treated as invalid (a reward with no cost AND no points isn't a
    // reward), but we surface skipped rows to the user instead of silently
    // dropping them — otherwise highlighted "edited" rows can disappear on
    // save with no feedback.
    const allEdits = Array.from(editedRowsRef.current.values());
    const skippedRows = allEdits.filter((row) => row.pointsPrice <= 0 && row.cashPrice <= 0);
    const entries = allEdits
      .filter((row) => row.pointsPrice > 0 || row.cashPrice > 0)
      .map((row) => ({
        itemType: 'flight' as const,
        itemSubType: 'charter',
        itemReference: row.journeyKey,
        itemSubReference: '',
        pointsPrice: row.pointsPrice,
        cashPrice: row.cashPrice,
        cashCurrency: row.cashCurrency,
        // `NOT_CONFIGURED` was filtered out of the status dropdown, but guard
        // here in case a legacy edited-row state still carries it.
        status: row.status === 'NOT_CONFIGURED' ? ('ACTIVE' as const) : row.status,
      }));

    if (entries.length === 0) {
      enqueueSnackbar('No valid entries to save — points price or cash price must be greater than 0.', {
        variant: 'warning',
      });
      return;
    }

    savingRef.current = true;
    setIsSaving(true);
    try {
      await upsertEntriesMutation.mutateAsync({ catalogId: flightsCatalogId, entries });
      if (skippedRows.length > 0) {
        enqueueSnackbar(
          `${entries.length} entries saved. ${skippedRows.length} skipped — points price and cash price were both 0.`,
          { variant: 'warning' },
        );
      } else {
        enqueueSnackbar(`${entries.length} reward entries saved successfully.`, { variant: 'success' });
      }
      // The write succeeded even if the invalidated read failed or still has old values.
      const refreshedState = entriesFilters
        ? queryClient.getQueryState<Array<RewardsCatalogEntry>>(rewardsQueryKeys.entriesList(entriesFilters))
        : undefined;
      // Merge this read once, preserving the values confirmed by the successful write.
      savedReadRef.current = refreshedState?.dataUpdatedAt ?? null;
      const refreshedRows = buildPricingRows(filteredFlights, refreshedState?.data ?? [], searchFilters.cashCurrency);
      setBaseRows(
        refreshedRows.map((row) => {
          const saved = entries.find((entry) => entry.itemReference === row.journeyKey);
          return saved
            ? {
                ...row,
                pointsPrice: saved.pointsPrice,
                cashPrice: saved.cashPrice,
                cashCurrency: saved.cashCurrency,
                status: saved.status,
              }
            : row;
        }),
      );
      setRefreshFailed(Boolean(refreshedState?.error));
      replaceDraft(new Map(skippedRows.map((row) => [row.journeyKey, row])));
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to save reward entries.', { variant: 'error' });
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [
    flightsCatalogId,
    commitActiveCell,
    upsertEntriesMutation,
    enqueueSnackbar,
    entriesFilters,
    queryClient,
    filteredFlights,
    searchFilters,
    replaceDraft,
  ]);

  // --- Grid columns ---
  const columns = useMemo<Array<GridColDef<ClassicRewardsPricingRow>>>(
    () => [
      {
        field: 'departureDate',
        headerName: 'Departure',
        width: 120,
        valueFormatter: (value) => formatDisplayDate(String(value)),
      },
      {
        field: 'route',
        headerName: 'Route',
        width: 110,
      },
      {
        field: 'cabinClass',
        headerName: 'Cabin',
        minWidth: 160,
        flex: 1,
      },
      {
        field: 'pointsPrice',
        headerName: 'Points Price',
        // Actions must read the typed value, rather than a debounced previous value.
        renderEditCell: (params) => <GridEditInputCell {...params} debounceMs={0} />,
        width: 130,
        type: 'number',
        editable: true,
        align: 'right',
        headerAlign: 'right',
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error: params.props.value != null && params.props.value < 0,
        }),
      },
      {
        field: 'cashPrice',
        headerName: 'Cash Gap ($)',
        renderEditCell: (params) => <GridEditInputCell {...params} debounceMs={0} />,
        width: 130,
        type: 'number',
        editable: true,
        align: 'right',
        headerAlign: 'right',
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error: params.props.value != null && params.props.value < 0,
        }),
      },
      {
        field: 'cashCurrency',
        headerName: 'Currency',
        width: 90,
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 140,
        flex: 1,
        // Only allow selecting real backend statuses. `NOT_CONFIGURED` is a
        // synthetic display-only value for unconfigured rows; if it were in
        // `valueOptions` users could pick it but the save handler would
        // silently coerce it to ACTIVE — confusing UX.
        editable: true,
        type: 'singleSelect',
        valueOptions: STATUS_OPTIONS.map((opt) => opt.value),
        renderCell: (params) => {
          if (params.value === 'NOT_CONFIGURED') {
            return <Chip label="Not Configured" size="small" variant="outlined" />;
          }
          if (params.value === 'ACTIVE') {
            return <Chip label="Active" size="small" color="success" />;
          }
          return <Chip label="Inactive" size="small" color="default" />;
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (params) =>
          params.row.entryId ? (
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setInventoryEntryId(params.row.entryId);
                setInventoryEntryLabel(`${params.row.route} ${params.row.departureDate} — ${params.row.cabinClass}`);
              }}
            >
              Inventory
            </Button>
          ) : null,
      },
    ],
    [],
  );

  const isLoading = charterQuery.isFetching || catalogsQuery.isFetching || entriesQuery.isFetching;
  const filtersChanged = Boolean(
    selectedCurrency !== searchFilters.cashCurrency ||
    ROUTE_OPTIONS[selectedRoute]?.departingDepAirport !== searchFilters.departingDepAirport ||
    ROUTE_OPTIONS[selectedRoute]?.departingArrAirport !== searchFilters.departingArrAirport ||
    CABIN_CLASSES.some((cabin) => selectedCabinClasses.includes(cabin) !== searchFilters.cabinClasses.includes(cabin)),
  );
  const appliedFilterSummary = [
    `${searchFilters.departingDepAirport} ↔ ${searchFilters.departingArrAirport}`,
    `New entries in ${searchFilters.cashCurrency}`,
    searchFilters.cabinClasses.length === CABIN_CLASSES.length
      ? 'All cabins'
      : searchFilters.cabinClasses.map((cabin) => CABIN_CLASS_LABELS[cabin]).join(', '),
  ].join(' · ');

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h4" fontWeight={600}>
          Classic Rewards
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure reward pricing (points + cash gap) per charter flight leg and cabin class.
        </Typography>
      </Stack>

      {/* --- Search filters --- */}
      <Card variant="outlined">
        <CardHeader title="Search departures" sx={{ pb: 0 }} />
        <CardContent>
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Route"
                  disabled={isSaving}
                  value={selectedRoute}
                  onChange={(e) => setSelectedRoute(Number(e.target.value))}
                >
                  {ROUTE_OPTIONS.map((route, index) => (
                    <MenuItem key={index} value={index}>
                      {route.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Currency"
                  disabled={isSaving}
                  helperText="Used for new entries. Existing entries keep their currency."
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                >
                  {CURRENCY_OPTIONS.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  select
                  SelectProps={{
                    multiple: true,
                    value: selectedCabinClasses,
                    renderValue: (selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as Array<FareFamilyCabinClass>).map((value) => (
                          <Chip key={value} size="small" label={CABIN_CLASS_LABELS[value]} />
                        ))}
                      </Box>
                    ),
                  }}
                  fullWidth
                  size="small"
                  label="Cabin classes"
                  disabled={isSaving}
                  error={!selectedCabinClasses.length}
                  helperText={!selectedCabinClasses.length ? 'Select at least one cabin class.' : undefined}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedCabinClasses(
                      typeof value === 'string'
                        ? (value.split(',') as Array<FareFamilyCabinClass>)
                        : (value as Array<FareFamilyCabinClass>),
                    );
                  }}
                >
                  {CABIN_CLASSES.map((key) => (
                    <MenuItem key={key} value={key}>
                      {CABIN_CLASS_LABELS[key]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Button
                variant="contained"
                onPointerDown={handleActionPointerDown}
                onClick={handleSearch}
                disabled={isLoading || isSaving || selectedCabinClasses.length === 0}
              >
                Search
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {!flightsCatalogId && catalogs && catalogs.length === 0 && (
        <Alert severity="warning">
          No active flight rewards catalog found. Create one in svc-lux-loyalty before configuring entries.
        </Alert>
      )}

      <Card variant="outlined">
        <CardHeader title="Results" subheader={appliedFilterSummary} sx={{ pb: 0 }} />
        <CardContent>
          <Stack spacing={2}>
            {filtersChanged && (
              <Typography variant="body2" color="text.secondary">
                Filters changed. Search to update results.
              </Typography>
            )}
            {refreshFailed && (
              <Alert severity="warning">
                {hasPendingEdits ? 'Previous changes were saved' : 'Your changes were saved'}, but results could not be
                refreshed. Search again to refresh.
              </Alert>
            )}
            {charterQuery.error && (
              <Alert severity="error">
                {charterQuery.error instanceof Error ? charterQuery.error.message : 'Failed to fetch flight data.'}
              </Alert>
            )}
            {catalogsQuery.error && <Alert severity="error">{catalogsQuery.error.message}</Alert>}
            {!refreshFailed && entriesQuery.error && <Alert severity="error">{entriesQuery.error.message}</Alert>}
            <Box
              sx={{
                width: '100%',
                height: rows.length <= MAX_AUTO_HEIGHT_ROWS ? 'auto' : 'clamp(320px, 55vh, 620px)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ flex: rows.length <= MAX_AUTO_HEIGHT_ROWS ? 'initial' : 1, minHeight: 0 }}>
                <DataGrid
                  apiRef={gridApiRef}
                  rows={rows}
                  columns={columns}
                  loading={isLoading}
                  autoHeight={rows.length <= MAX_AUTO_HEIGHT_ROWS}
                  paginationModel={paginationModel}
                  onPaginationModelChange={handlePaginationModelChange}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  initialState={{ sorting: { sortModel: [{ field: 'departureDate', sort: 'asc' }] } }}
                  localeText={{ noRowsLabel: 'No reward pricing found for the selected route and cabins.' }}
                  hideFooterSelectedRowCount
                  disableRowSelectionOnClick
                  cellModesModel={cellModesModel}
                  onCellModesModelChange={setCellModesModel}
                  onCellEditStop={(params) => {
                    // Focus can leave the grid before processRowUpdate receives the edit.
                    setIsCommittingCell(params.reason !== GridCellEditStopReasons.escapeKeyDown);
                  }}
                  isCellEditable={() => Boolean(flightsCatalogId) && !isSaving && !isLoading}
                  onCellClick={handleCellClick}
                  processRowUpdate={processRowUpdate}
                  onProcessRowUpdateError={(error) =>
                    enqueueSnackbar(error instanceof Error ? error.message : 'Failed to update cell.', {
                      variant: 'error',
                    })
                  }
                  getCellClassName={getCellClassName}
                  sx={{
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    '& .cell-modified': { bgcolor: 'warning.light' },
                  }}
                />
              </Box>
              {rows.length > 0 && (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                  gap={2}
                  sx={{
                    px: 2,
                    py: 1.5,
                    border: 1,
                    borderTop: 0,
                    borderColor: 'divider',
                    borderBottomLeftRadius: 1,
                    borderBottomRightRadius: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography variant="body2" color="text.secondary" aria-live="polite">
                    {editedRows.size
                      ? `${editedRows.size} reward ${editedRows.size === 1 ? 'entry' : 'entries'} with unsaved changes across all pages`
                      : hasActiveCell
                        ? 'Finish editing the cell, or save to apply it.'
                        : 'Edit points, cash or status in the table to make changes.'}
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="text"
                      onPointerDown={handleActionPointerDown}
                      disabled={!hasPendingEdits || isSaving}
                      onClick={() => setPendingAction({ type: 'discard' })}
                    >
                      Discard edits
                    </Button>
                    <LoadingButton
                      variant="contained"
                      onPointerDown={handleActionPointerDown}
                      loading={isSaving}
                      onClick={handleSave}
                      disabled={!hasPendingEdits || !flightsCatalogId || isLoading}
                    >
                      Save changes
                    </LoadingButton>
                  </Stack>
                </Stack>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(pendingAction)}
        onClose={() => setPendingAction(null)}
        aria-labelledby="classic-rewards-discard-title"
        aria-describedby="classic-rewards-discard-description"
      >
        <DialogTitle id="classic-rewards-discard-title">Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText id="classic-rewards-discard-description">
            {pendingAction?.type === 'search'
              ? 'Discard your reward pricing edits and show the new search results?'
              : pendingAction?.type === 'navigate'
                ? 'Discard your reward pricing edits and leave this page?'
                : 'Discard your reward pricing edits and restore the last saved values?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingAction(null)} autoFocus>
            Keep editing
          </Button>
          <Button variant="contained" onClick={confirmDiscard}>
            {pendingAction?.type === 'search'
              ? 'Discard and search'
              : pendingAction?.type === 'navigate'
                ? 'Discard and leave'
                : 'Discard changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- Inventory dialog --- */}
      <ClassicRewardsInventoryDialog
        open={Boolean(inventoryEntryId)}
        onClose={() => setInventoryEntryId(null)}
        entryId={inventoryEntryId}
        entryLabel={inventoryEntryLabel}
      />
    </Stack>
  );
}
